import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail, pollJob, newIdemKey, idemHeaders } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import { Loader2, ShieldCheck, AlertTriangle, Ban, HelpCircle, ChevronDown, ChevronRight, CheckCheck } from "lucide-react";
import { toast } from "sonner";
import { CONTENT_STATUS_STYLES } from "./constants";

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4" data-testid={`inbox-stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${tint}`}><Icon className="h-4 w-4" /></div>
      <p className="mt-2 font-heading text-xl font-bold">{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

const riskBadge = (c) => {
  const r = c?.result;
  if (r === "Warning") return <span className="rounded-full px-2 py-0.5 text-xs bg-amber-100 text-amber-700">Warning</span>;
  if (c?.confirm_fact) return <span className="rounded-full px-2 py-0.5 text-xs bg-blue-100 text-blue-700">Confirm Fact</span>;
  return <span className="rounded-full px-2 py-0.5 text-xs bg-emerald-100 text-emerald-700">Low risk</span>;
};

export default function ApprovalInbox() {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/marketing/approval-inbox").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  const bulkLowRisk = async () => {
    setBusy(true);
    try { const r = await api.post("/marketing/content/bulk-approve-low-risk", {}); toast.success(`Approved ${r.data.approved} low-risk item(s)`); setSelected({}); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const createMediaBulk = async () => {
    setBusy(true);
    try { const r = await api.post("/marketing/content/create-media-bulk-low-risk", {}); toast.success(`Started media for ${r.data.started} approved low-risk item(s) — see Media Review`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const bulk = async (action) => {
    if (selectedIds.length === 0) { toast.error("Select at least one item"); return; }
    setBusy(true);
    try {
      if (action === "regenerate") {
        const jobIds = [];
        for (const id of selectedIds) {
          const { data } = await api.post(`/marketing/content/${id}/regenerate`, { parts: ["all"] }, idemHeaders(newIdemKey()));
          jobIds.push(data.job_id);
        }
        await Promise.all(jobIds.map((jid) => pollJob(jid).catch(() => null)));
        toast.success("Regenerated selected");
      } else {
        const r = await api.post(`/marketing/content/bulk-action?action=${action}`, { ids: selectedIds });
        toast.success(`Updated ${r.data.updated} item(s)`);
      }
      setSelected({}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const openReject = () => {
    if (selectedIds.length === 0) { toast.error("Select at least one item"); return; }
    setRejectReason(""); setRejectOpen(true);
  };
  const doRejectSelected = async () => {
    if (!rejectReason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/content/bulk-action?action=reject`, { ids: selectedIds, reason: rejectReason });
      toast.success(`Rejected ${r.data.updated} item(s)`);
      setRejectOpen(false); setSelected({}); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  if (data === null) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#581C87]" /></div>;
  if (data === false) return <p className="text-sm text-red-600">Failed to load Approval Inbox.</p>;

  return (
    <div data-testid="approval-inbox-page">
      <h2 className="font-heading text-2xl font-bold">Approval Inbox</h2>
      <p className="text-sm text-gray-500 mt-1">Review weekly batches quickly. Approval does not publish content in Phase 2.</p>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
        <Stat icon={ShieldCheck} label="Low Risk" value={data.low_risk} tint="bg-emerald-50 text-emerald-600" />
        <Stat icon={AlertTriangle} label="Warnings" value={data.warnings} tint="bg-amber-50 text-amber-600" />
        <Stat icon={HelpCircle} label="Confirm Fact" value={data.confirm_fact} tint="bg-blue-50 text-blue-600" />
        <Stat icon={Ban} label="Blocked" value={data.blocked} tint="bg-red-50 text-red-600" />
        <Stat icon={CheckCheck} label="Total Pending" value={data.total_pending} tint="bg-[#F3E8FF] text-[#6B21A8]" />
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        <Button onClick={bulkLowRisk} disabled={busy || data.low_risk === 0} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-low-risk-button"><CheckCheck className="h-4 w-4 mr-2" /> Approve All Low-Risk Items</Button>
        <Button onClick={createMediaBulk} disabled={busy} className="bg-[#581C87] hover:bg-[#6B21A8] text-white" data-testid="create-media-bulk-button"><CheckCheck className="h-4 w-4 mr-2" /> Create Media (Approved Low-Risk)</Button>
        <Button onClick={() => bulk("approve")} disabled={busy} variant="outline" className="border-gray-200" data-testid="approve-selected-button">Approve Selected</Button>
        <Button onClick={openReject} disabled={busy} variant="outline" className="border-gray-200 hover:text-red-600 hover:border-red-500" data-testid="reject-selected-button">Reject Selected</Button>
        <Button onClick={() => bulk("regenerate")} disabled={busy} variant="outline" className="border-gray-200" data-testid="regenerate-selected-button">Regenerate Selected</Button>
        <Button onClick={() => bulk("hold")} disabled={busy} variant="outline" className="border-gray-200" data-testid="hold-selected-button">Hold Selected</Button>
      </div>

      <div className="mt-6 space-y-4">
        {data.batches.length === 0 && <div className="bg-white border border-dashed border-gray-200 rounded-xl px-6 py-10 text-center text-gray-400 text-sm" data-testid="inbox-empty">No items awaiting review. Launch or generate a campaign batch.</div>}
        {data.batches.map((batch) => (
          <div key={batch.batch_id} className="bg-white border border-gray-200 rounded-xl overflow-hidden" data-testid={`inbox-batch-${batch.batch_id}`}>
            <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">Weekly Batch</span>
              <span className="text-xs text-gray-500">{batch.core_concepts} core concepts · {batch.total_posts} platform posts</span>
            </div>
            {batch.concepts.map((concept) => {
              const first = concept.variants[0];
              const isOpen = expanded[concept.concept_id];
              return (
                <div key={concept.concept_id} className="border-b border-gray-100">
                  <button onClick={() => setExpanded((s) => ({ ...s, [concept.concept_id]: !s[concept.concept_id] }))} className="w-full flex items-center gap-2 px-6 py-3 hover:bg-gray-50 text-left" data-testid={`concept-toggle-${concept.concept_id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />}
                    <span className="font-medium text-sm text-gray-900">{first?.title}</span>
                    <span className="text-xs text-gray-400">· {concept.variants.length} platform variants</span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-3 space-y-2">
                      {concept.variants.map((v) => (
                        <div key={v.id} className="flex items-center gap-3 rounded-lg border border-gray-100 px-3 py-2" data-testid={`inbox-item-${v.id}`}>
                          <input type="checkbox" checked={!!selected[v.id]} onChange={() => toggleSel(v.id)} data-testid={`select-item-${v.id}`} className="accent-[#581C87]" />
                          <span className="text-xs font-medium text-gray-600 w-20">{v.platform}</span>
                          <span className="text-sm text-gray-800 truncate flex-1">{v.hook || v.caption || "—"}</span>
                          {riskBadge(v.compliance)}
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/marketing/content/${v.id}`)} data-testid={`open-item-${v.id}`} className="text-[#581C87]">Open</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {data.recent_approved.length > 0 && (
        <div className="mt-8">
          <h3 className="font-heading font-bold text-sm text-gray-500 uppercase tracking-widest">Recently Approved</h3>
          <div className="mt-3 bg-white border border-gray-200 rounded-xl overflow-hidden">
            {data.recent_approved.map((r) => (
              <button key={r.id} onClick={() => navigate(`/marketing/content/${r.id}`)} className="w-full text-left flex items-center justify-between px-6 py-3 border-b border-gray-50 hover:bg-gray-50">
                <span className="text-sm text-gray-800 truncate">{r.title} · {r.platform}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${CONTENT_STATUS_STYLES.Approved}`}>Approved</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="max-w-md" data-testid="reject-selected-dialog">
          <DialogHeader><DialogTitle className="font-heading">Reject Selected Items</DialogTitle></DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Reason (applied to all selected) *</Label>
            <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} data-testid="reject-selected-reason" className="focus-visible:ring-red-500" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>Cancel</Button>
            <Button onClick={doRejectSelected} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="confirm-reject-selected-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
